<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
    <CompanyAppLayout>
      <!-- Page Header -->
      <div class="pt-8 pb-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold text-white mb-2">Company Settings</h1>
            <p class="text-slate-400">Manage your company preferences, notifications, and security settings</p>
          </div>
          <div class="flex items-center space-x-3">
            <button @click="saveSettings" class="btn-primary">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
              </svg>
              Save Changes
            </button>
            <button @click="resetToDefaults" class="btn-secondary">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
              </svg>
              Reset to Defaults
            </button>
          </div>
        </div>
      </div>

      <!-- Settings Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Main Settings -->
        <div class="lg:col-span-2 space-y-8">
          <!-- General Settings -->
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
            <h2 class="text-xl font-semibold text-white mb-6">General Settings</h2>
            
            <div class="space-y-6">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-2">Default Timezone</label>
                  <select v-model="settings.timezone" class="form-select w-full">
                    <option value="UTC">UTC</option>
                    <option value="EST">Eastern Time</option>
                    <option value="CST">Central Time</option>
                    <option value="MST">Mountain Time</option>
                    <option value="PST">Pacific Time</option>
                    <option value="AKST">Alaska Time</option>
                    <option value="HST">Hawaii Time</option>
                  </select>
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-2">Date Format</label>
                  <select v-model="settings.dateFormat" class="form-select w-full">
                    <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                    <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                  </select>
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-2">Currency</label>
                  <select v-model="settings.currency" class="form-select w-full">
                    <option value="USD">USD ($)</option>
                    <option value="CAD">CAD (C$)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="GBP">GBP (£)</option>
                  </select>
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-2">Language</label>
                  <select v-model="settings.language" class="form-select w-full">
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                  </select>
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Company Logo</label>
                <div class="flex items-center space-x-4">
                  <div class="w-16 h-16 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-lg flex items-center justify-center overflow-hidden">
                    <img 
                      v-if="settings.logoUrl" 
                      :src="settings.logoUrl" 
                      alt="Company Logo" 
                      class="w-full h-full object-cover"
                    />
                    <span v-else class="text-xl font-bold text-white">{{ companyName.charAt(0) }}</span>
                  </div>
                  <button @click="triggerLogoUpload" class="btn-secondary">
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                    Upload Logo
                  </button>
                  <input 
                    ref="logoInput" 
                    type="file" 
                    accept="image/*" 
                    @change="handleLogoUpload" 
                    class="hidden"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Notification Settings -->
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
            <h2 class="text-xl font-semibold text-white mb-6">Notification Settings</h2>
            
            <div class="space-y-6">
              <div class="space-y-4">
                <h3 class="text-lg font-medium text-white">Email Notifications</h3>
                
                <div class="space-y-3">
                  <label class="flex items-center">
                    <input v-model="settings.notifications.email.newApplications" type="checkbox" class="form-checkbox" />
                    <span class="ml-3 text-sm text-slate-300">New job applications</span>
                  </label>
                  
                  <label class="flex items-center">
                    <input v-model="settings.notifications.email.paymentReceived" type="checkbox" class="form-checkbox" />
                    <span class="ml-3 text-sm text-slate-300">Payment received</span>
                  </label>
                  
                  <label class="flex items-center">
                    <input v-model="settings.notifications.email.safetyAlerts" type="checkbox" class="form-checkbox" />
                    <span class="ml-3 text-sm text-slate-300">Safety alerts and incidents</span>
                  </label>
                  
                  <label class="flex items-center">
                    <input v-model="settings.notifications.email.timesheetApproval" type="checkbox" class="form-checkbox" />
                    <span class="ml-3 text-sm text-slate-300">Timesheet approval requests</span>
                  </label>
                  
                  <label class="flex items-center">
                    <input v-model="settings.notifications.email.taxReminders" type="checkbox" class="form-checkbox" />
                    <span class="ml-3 text-sm text-slate-300">Tax filing reminders</span>
                  </label>
                </div>
              </div>

              <div class="border-t border-slate-700/50 pt-6">
                <h3 class="text-lg font-medium text-white mb-4">Push Notifications</h3>
                
                <div class="space-y-3">
                  <label class="flex items-center">
                    <input v-model="settings.notifications.push.urgentAlerts" type="checkbox" class="form-checkbox" />
                    <span class="ml-3 text-sm text-slate-300">Urgent safety alerts</span>
                  </label>
                  
                  <label class="flex items-center">
                    <input v-model="settings.notifications.push.scheduleChanges" type="checkbox" class="form-checkbox" />
                    <span class="ml-3 text-sm text-slate-300">Schedule changes</span>
                  </label>
                  
                  <label class="flex items-center">
                    <input v-model="settings.notifications.push.paymentUpdates" type="checkbox" class="form-checkbox" />
                    <span class="ml-3 text-sm text-slate-300">Payment updates</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- Security Settings -->
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
            <h2 class="text-xl font-semibold text-white mb-6">Security Settings</h2>
            
            <div class="space-y-6">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-2">Session Timeout (minutes)</label>
                  <input v-model="settings.security.sessionTimeout" type="number" class="form-input w-full" min="5" max="480" />
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-2">Failed Login Attempts</label>
                  <input v-model="settings.security.maxLoginAttempts" type="number" class="form-input w-full" min="3" max="10" />
                </div>
              </div>

              <div class="space-y-3">
                <label class="flex items-center">
                  <input v-model="settings.security.twoFactorAuth" type="checkbox" class="form-checkbox" />
                  <span class="ml-3 text-sm text-slate-300">Enable Two-Factor Authentication</span>
                </label>
                
                <label class="flex items-center">
                  <input v-model="settings.security.loginNotifications" type="checkbox" class="form-checkbox" />
                  <span class="ml-3 text-sm text-slate-300">Notify on new login</span>
                </label>
                
                <label class="flex items-center">
                  <input v-model="settings.security.passwordExpiry" type="checkbox" class="form-checkbox" />
                  <span class="ml-3 text-sm text-slate-300">Password expiry notifications</span>
                </label>
              </div>

              <div class="border-t border-slate-700/50 pt-6">
                <h3 class="text-lg font-medium text-white mb-4">Data Privacy</h3>
                
                <div class="space-y-3">
                  <label class="flex items-center">
                    <input v-model="settings.privacy.analyticsTracking" type="checkbox" class="form-checkbox" />
                    <span class="ml-3 text-sm text-slate-300">Allow analytics tracking</span>
                  </label>
                  
                  <label class="flex items-center">
                    <input v-model="settings.privacy.marketingEmails" type="checkbox" class="form-checkbox" />
                    <span class="ml-3 text-sm text-slate-300">Receive marketing emails</span>
                  </label>
                  
                  <label class="flex items-center">
                    <input v-model="settings.privacy.dataSharing" type="checkbox" class="form-checkbox" />
                    <span class="ml-3 text-sm text-slate-300">Allow data sharing with partners</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- Integration Settings -->
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
            <h2 class="text-xl font-semibold text-white mb-6">Integration Settings</h2>
            
            <div class="space-y-6">
              <div class="space-y-4">
                <h3 class="text-lg font-medium text-white">Third-Party Integrations</h3>
                
                <div class="space-y-4">
                  <div class="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
                    <div class="flex items-center space-x-3">
                      <div class="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                        <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                        </svg>
                      </div>
                      <div>
                        <h4 class="text-sm font-medium text-white">QuickBooks</h4>
                        <p class="text-xs text-slate-400">Accounting integration</p>
                      </div>
                    </div>
                    <button @click="toggleIntegration('quickbooks')" :class="[
                      'px-3 py-1 rounded-full text-xs font-medium',
                      settings.integrations.quickbooks ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600/50 text-slate-400'
                    ]">
                      {{ settings.integrations.quickbooks ? 'Connected' : 'Connect' }}
                    </button>
                  </div>
                  
                  <div class="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
                    <div class="flex items-center space-x-3">
                      <div class="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                        <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                        </svg>
                      </div>
                      <div>
                        <h4 class="text-sm font-medium text-white">Stripe</h4>
                        <p class="text-xs text-slate-400">Payment processing</p>
                      </div>
                    </div>
                    <button @click="toggleIntegration('stripe')" :class="[
                      'px-3 py-1 rounded-full text-xs font-medium',
                      settings.integrations.stripe ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600/50 text-slate-400'
                    ]">
                      {{ settings.integrations.stripe ? 'Connected' : 'Connect' }}
                    </button>
                  </div>
                  
                  <div class="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
                    <div class="flex items-center space-x-3">
                      <div class="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                        <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                        </svg>
                      </div>
                      <div>
                        <h4 class="text-sm font-medium text-white">Slack</h4>
                        <p class="text-xs text-slate-400">Team communication</p>
                      </div>
                    </div>
                    <button @click="toggleIntegration('slack')" :class="[
                      'px-3 py-1 rounded-full text-xs font-medium',
                      settings.integrations.slack ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600/50 text-slate-400'
                    ]">
                      {{ settings.integrations.slack ? 'Connected' : 'Connect' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Sidebar -->
        <div class="lg:col-span-1 space-y-6">
          <!-- Quick Actions -->
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
            <h3 class="text-lg font-semibold text-white mb-4">Quick Actions</h3>
            
            <div class="space-y-3">
              <button @click="exportSettings" class="quick-action-card">
                <svg class="w-6 h-6 text-cyan-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                <span class="text-sm font-medium text-white">Export Settings</span>
              </button>
              
              <button @click="importSettings" class="quick-action-card">
                <svg class="w-6 h-6 text-emerald-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
                </svg>
                <span class="text-sm font-medium text-white">Import Settings</span>
              </button>
              
              <button @click="backupData" class="quick-action-card">
                <svg class="w-6 h-6 text-amber-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"/>
                </svg>
                <span class="text-sm font-medium text-white">Backup Data</span>
              </button>
            </div>
          </div>

          <!-- System Info -->
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
            <h3 class="text-lg font-semibold text-white mb-4">System Information</h3>
            
            <div class="space-y-3">
              <div class="flex items-center justify-between">
                <span class="text-sm text-slate-300">Last Login</span>
                <span class="text-sm text-white">{{ formatDate(systemInfo.lastLogin) }}</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm text-slate-300">Account Created</span>
                <span class="text-sm text-white">{{ formatDate(systemInfo.createdAt) }}</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm text-slate-300">Storage Used</span>
                <span class="text-sm text-white">{{ systemInfo.storageUsed }}</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm text-slate-300">API Calls</span>
                <span class="text-sm text-white">{{ systemInfo.apiCalls }}/month</span>
              </div>
            </div>
          </div>

          <!-- Danger Zone -->
          <div class="bg-red-900/20 backdrop-blur-sm border border-red-700/50 rounded-2xl p-6">
            <h3 class="text-lg font-semibold text-red-400 mb-4">Danger Zone</h3>
            
            <div class="space-y-3">
              <button @click="clearCache" class="danger-action-card">
                <svg class="w-6 h-6 text-amber-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                </svg>
                <span class="text-sm font-medium text-white">Clear Cache</span>
              </button>
              
              <button @click="deactivateAccount" class="danger-action-card">
                <svg class="w-6 h-6 text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                </svg>
                <span class="text-sm font-medium text-white">Deactivate Account</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </CompanyAppLayout>
  </div>
</template>

<script setup lang="ts">
import CompanyAppLayout from '@/components/layout/CompanyAppLayout.vue'
import { useUserStore } from '@/stores/userStore'
import { onMounted, ref } from 'vue'

const userStore = useUserStore()

// Reactive data
const logoInput = ref(null)
const companyName = ref('OilBuddy Company')

const settings = ref({
  timezone: 'UTC',
  dateFormat: 'MM/DD/YYYY',
  currency: 'USD',
  language: 'en',
  logoUrl: '',
  notifications: {
    email: {
      newApplications: true,
      paymentReceived: true,
      safetyAlerts: true,
      timesheetApproval: true,
      taxReminders: true
    },
    push: {
      urgentAlerts: true,
      scheduleChanges: true,
      paymentUpdates: false
    }
  },
  security: {
    sessionTimeout: 30,
    maxLoginAttempts: 5,
    twoFactorAuth: false,
    loginNotifications: true,
    passwordExpiry: true
  },
  privacy: {
    analyticsTracking: true,
    marketingEmails: false,
    dataSharing: false
  },
  integrations: {
    quickbooks: false,
    stripe: true,
    slack: false
  }
})

const systemInfo = ref({
  lastLogin: '',
  createdAt: '',
  storageUsed: '0 MB',
  apiCalls: 0
})

// Methods
const fetchSettings = async () => {
  try {
    const response = await fetch('/api/company/settings', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      settings.value = { ...settings.value, ...data.settings }
      systemInfo.value = data.systemInfo
    }
  } catch (error) {
    console.error('Error fetching settings:', error)
  }
}

const saveSettings = async () => {
  try {
    const response = await fetch('/api/company/update-settings', {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(settings.value)
    })
    
    if (response.ok) {
      console.log('Settings saved successfully')
    }
  } catch (error) {
    console.error('Error saving settings:', error)
  }
}

const resetToDefaults = () => {
  settings.value = {
    timezone: 'UTC',
    dateFormat: 'MM/DD/YYYY',
    currency: 'USD',
    language: 'en',
    logoUrl: '',
    notifications: {
      email: {
        newApplications: true,
        paymentReceived: true,
        safetyAlerts: true,
        timesheetApproval: true,
        taxReminders: true
      },
      push: {
        urgentAlerts: true,
        scheduleChanges: true,
        paymentUpdates: false
      }
    },
    security: {
      sessionTimeout: 30,
      maxLoginAttempts: 5,
      twoFactorAuth: false,
      loginNotifications: true,
      passwordExpiry: true
    },
    privacy: {
      analyticsTracking: true,
      marketingEmails: false,
      dataSharing: false
    },
    integrations: {
      quickbooks: false,
      stripe: true,
      slack: false
    }
  }
}

const triggerLogoUpload = () => {
  logoInput.value.click()
}

const handleLogoUpload = async (event: any) => {
  const file = event.target.files[0]
  if (!file) return

  const formData = new FormData()
  formData.append('logo', file)

  try {
    const response = await fetch('/api/company/upload-logo', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`
      },
      body: formData
    })
    
    if (response.ok) {
      const data = await response.json()
      settings.value.logoUrl = data.logoUrl
    }
  } catch (error) {
    console.error('Error uploading logo:', error)
  }
}

const toggleIntegration = (integration: string) => {
  settings.value.integrations[integration] = !settings.value.integrations[integration]
}

const exportSettings = () => {
  const dataStr = JSON.stringify(settings.value, null, 2)
  const blob = new Blob([dataStr], { type: 'application/json' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'company-settings.json'
  a.click()
  window.URL.revokeObjectURL(url)
}

const importSettings = () => {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = '.json'
  input.onchange = (event: any) => {
    const file = event.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e: any) => {
        try {
          const importedSettings = JSON.parse(e.target.result)
          settings.value = { ...settings.value, ...importedSettings }
        } catch (error) {
          console.error('Error importing settings:', error)
        }
      }
      reader.readAsText(file)
    }
  }
  input.click()
}

const backupData = () => {
  // Implement backup functionality
  console.log('Backing up data...')
}

const clearCache = () => {
  // Implement cache clearing
  console.log('Clearing cache...')
}

const deactivateAccount = () => {
  // Implement account deactivation
  console.log('Deactivating account...')
}

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString()
}

// Lifecycle
onMounted(async () => {
  await fetchSettings()
})
</script>

<style scoped>
.quick-action-card, .danger-action-card {
  @apply flex items-center w-full p-3 rounded-lg transition-colors;
}

.quick-action-card {
  @apply bg-slate-700/30 hover:bg-slate-600/50;
}

.danger-action-card {
  @apply bg-red-800/30 hover:bg-red-700/50;
}

.btn-primary {
  @apply px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors flex items-center;
}

.btn-secondary {
  @apply px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 font-medium rounded-lg transition-colors flex items-center;
}

.form-input, .form-select {
  @apply bg-slate-700/50 border border-slate-600/50 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent;
}

.form-select {
  @apply min-w-[120px];
}

.form-checkbox {
  @apply w-4 h-4 text-emerald-500 bg-slate-700/50 border-slate-600/50 rounded focus:ring-emerald-500 focus:ring-2;
}
</style>
